import { Controller } from '@hotwired/stimulus'
import * as THREE from 'three'

/**
 * Interactive 3D Canvas Controller
 *
 * Creates a realistic 3D canvas with customizable depth and edge treatments:
 * - Upload images to display on canvas OR use provided src URL
 * - Adjustable canvas depth with slider
 * - Multiple edge types: mirrored image edges, solid colors (black/white)
 * - Interactive rotation and panning controls (mouse and touch)
 * - Zoom functionality with mouse wheel or pinch gesture
 */
export default class extends Controller {
  static targets = [
    'container',
    'imageInput',
    'sideTypeSelect',
    'depthSlider',
    'depthValue',
  ]

  static optionalTargets = [
    'imageInput',
    'sideTypeSelect',
    'depthSlider',
    'depthValue',
  ]

  static values = {
    src: String,
    sideType: { type: String, default: 'mirrored' },
  }

  // ===== INITIALIZATION =====

  connect() {
    // remove element and clean up when the modal is closed
    window.addEventListener('ProductDetailsImageModalClosed', () => {
      this.element.remove()
    })

    this.setup()
  }

  setup() {
    // Initialize Three.js variables
    this.scene = null
    this.camera = null
    this.renderer = null
    this.frameGroup = null

    // Mouse interaction state
    this.isDragging = false
    this.previousMousePosition = { x: 0, y: 0 }
    this.mouseButton = null

    // Touch interaction state
    this.touches = new Map() // Track multiple touches
    this.isRotating = false
    this.isPanning = false
    this.isPinching = false
    this.lastPinchDistance = 0
    this.previousTouchPosition = { x: 0, y: 0 }
    this.touchInteractionType = null // 'rotate', 'pan', or 'pinch'

    // Canvas properties
    this.frameDepth = 0.1 // Default canvas thickness
    this.currentTexture = null // Current image texture

    // Auto rotation
    this.autoRotate = true
    this.autoRotateSpeed = 0.005

    if (this.hasSideSelectorTarget) {
      this.sideTypeValue = this.sideTypeSelectTarget.value
    }

    this.init()
  }

  disconnect() {
    // Clean up Three.js resources to prevent memory leaks
    if (this.renderer) {
      this.renderer.dispose()
    }
    if (this.scene) {
      this.scene.clear()
    }

    // Clean up touch event listeners
    if (this.element) {
      this.element.removeEventListener('touchstart', this.onTouchStart)
      this.element.removeEventListener('touchmove', this.onTouchMove)
      this.element.removeEventListener('touchend', this.onTouchEnd)

      // Clean up mouse event listeners
      this.element.removeEventListener('mousedown', this.onMouseDown)
      this.element.removeEventListener('mousemove', this.onMouseMove)
      this.element.removeEventListener('mouseup', this.onMouseUp)
      this.element.removeEventListener('wheel', this.onWheel)
    }

    // Clean up other event listeners
    window.removeEventListener('resize', this.onWindowResize)
  }

  /**
   * Initialize the complete Three.js scene
   */
  init() {
    this.setupScene()
    this.setupCamera()
    this.setupRenderer()
    this.setupLighting()
    this.createInitialCanvas()
    this.setupControls()
    this.startAnimationLoop()
    this.setupEventListeners()
  }

  /**
   * Initialize the Three.js scene with white background
   */
  setupScene() {
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xffffff)
  }

  /**
   * Setup perspective camera positioned to view the canvas
   */
  setupCamera() {
    this.camera = new THREE.PerspectiveCamera(
      75, // Field of view
      this.containerTarget.clientWidth / this.containerTarget.clientHeight, // Aspect ratio
      0.1, // Near clipping plane
      1000, // Far clipping plane
    )

    // Calculate initial zoom based on container size and expected canvas size
    this.setInitialCameraPosition()
  }

  /**
   * Calculate and set optimal camera position based on container size
   */
  setInitialCameraPosition() {
    // const containerWidth = this.containerTarget.clientWidth
    // const containerHeight = this.containerTarget.clientHeight
    // const containerAspect = containerWidth / containerHeight

    // Estimate canvas size (this will be refined after image loads)
    let estimatedCanvasWidth = 2
    let estimatedCanvasHeight = 2

    if (this.currentTexture && this.currentTexture.image) {
      const imageAspect = this.currentTexture.image.width / this.currentTexture.image.height
      if (imageAspect > 1) {
        estimatedCanvasHeight = estimatedCanvasWidth / imageAspect
      }
      else {
        estimatedCanvasWidth = estimatedCanvasHeight * imageAspect
      }
    }

    // Calculate distance needed to fit canvas in view
    // Use field of view to determine how far back camera needs to be
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(estimatedCanvasWidth, estimatedCanvasHeight)

    // Calculate distance with some padding (multiply by 1.2 for 20% padding)
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.2

    // Ensure minimum distance for depth perception
    const minDistance = Math.max(distance, 1.5)

    this.camera.position.set(0, 0, minDistance)
  }

  /**
   * Initialize WebGL renderer with proper color management and shadows
   */
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })
    this.renderer.setSize(this.containerTarget.clientWidth, this.containerTarget.clientHeight)
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap // Soft shadows

    // Configure color management for proper image display
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping
    this.renderer.toneMappingExposure = 1.0

    this.containerTarget.appendChild(this.renderer.domElement)
  }

  /**
   * Setup lighting system for realistic canvas appearance
   * Uses ambient + directional lighting with shadows
   */
  setupLighting() {
    // Ambient light for overall scene illumination
    const ambientLight = new THREE.AmbientLight(0x606060, 3.2)
    this.scene.add(ambientLight)

    // Main directional light with shadow casting
    const directionalLight = new THREE.DirectionalLight(0xffffff, 3.2)
    directionalLight.position.set(2, 2, 5)
    directionalLight.castShadow = true
    this.scene.add(directionalLight)
  }

  /**
   * Create initial canvas with src image or placeholder
   */
  createInitialCanvas() {
    if (this.hasSrcValue && this.srcValue) {
      // Load image from src URL
      this.loadImageFromSrc(this.srcValue)
    }
    else {
      // Create placeholder canvas
      this.createFrame(null, 'mirrored')
    }
  }

  /**
   * Load image from src URL and create canvas
   * @param {string} srcUrl - URL of the image to load
   */
  loadImageFromSrc(srcUrl) {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Handle CORS if needed
    img.onload = () => {
      // Create Three.js texture with proper color settings
      const texture = new THREE.Texture(img)
      texture.needsUpdate = true
      texture.colorSpace = THREE.SRGBColorSpace
      texture.flipY = true // Proper orientation
      texture.premultiplyAlpha = false
      texture.format = THREE.RGBAFormat // Required for sRGB color space
      texture.type = THREE.UnsignedByteType // Required for sRGB

      this.currentTexture = texture

      // Create canvas with loaded image
      const currentSideType = this.sideTypeValue
      this.createFrame(texture, currentSideType)
    }
    img.onerror = (error) => {
      console.error(`Failed to load image from src: ${srcUrl}`, error)
      // Fall back to placeholder
      this.createFrame(null, 'mirrored')
    }
    img.src = srcUrl
  }

  /**
   * Start the render loop
   */
  startAnimationLoop() {
    this.animate()
  }

  /**
   * Setup window resize listener
   */
  setupEventListeners() {
    window.addEventListener('resize', this.onWindowResize)
  }

  // ===== CANVAS CONSTRUCTION =====

  /**
   * Create the complete 3D canvas structure
   * @param {THREE.Texture} imageTexture - Image to display (null for placeholder)
   * @param {string} sideType - Type of edges: 'mirrored', 'black', 'white'
   */
  createFrame(imageTexture, sideType) {
  // Remove existing canvas
    if (this.frameGroup) {
      this.scene.remove(this.frameGroup)
    }

    this.frameGroup = new THREE.Group()

    // Calculate canvas dimensions based on image aspect ratio
    const { frameWidth, frameHeight } = this.calculateDimensions(imageTexture)

    // Create front and back faces
    this.createCanvasFaces(imageTexture, frameWidth, frameHeight)

    // Create side edges based on selected type
    const finalSideType = sideType || 'mirrored'
    if (finalSideType === 'mirrored' && imageTexture) {
      this.createMirroredSides(imageTexture, frameWidth, frameHeight)
    }
    else {
      this.createSolidSides(frameWidth, frameHeight, finalSideType)
    }

    this.scene.add(this.frameGroup)

    // Adjust camera position based on actual canvas size
    this.adjustCameraForCanvas(frameWidth, frameHeight)
  }

  /**
   * Adjust camera position based on actual canvas dimensions
   * @param {number} frameWidth - Actual canvas width
   * @param {number} frameHeight - Actual canvas height
   */
  adjustCameraForCanvas(frameWidth, frameHeight) {
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(frameWidth, frameHeight)

    // Calculate optimal distance with padding
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.2
    const minDistance = Math.max(distance, 1.5)

    // Smoothly animate to new position if camera is already positioned
    if (this.camera.position.z !== 3) { // 3 is the default initial position
    // Animate camera to new position
      const startZ = this.camera.position.z
      const targetZ = minDistance
      const duration = 1000 // 1 second
      const startTime = Date.now()

      const animateCamera = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Smooth easing function
        const easeProgress = progress * progress * (3 - 2 * progress)

        this.camera.position.z = startZ + (targetZ - startZ) * easeProgress

        if (progress < 1) {
          requestAnimationFrame(animateCamera)
        }
      }

      animateCamera()
    }
    else {
    // Set directly if this is the initial setup
      this.camera.position.z = minDistance
    }
  }

  /**
   * Calculate canvas dimensions based on image aspect ratio
   * @param {THREE.Texture} imageTexture - Image texture (can be null)
   * @returns {Object} Dimensions and aspect ratio
   */
  calculateDimensions(imageTexture) {
    let aspectRatio = 1
    let frameWidth = 2
    let frameHeight = 2

    if (imageTexture) {
      aspectRatio = imageTexture.image.width / imageTexture.image.height
      if (aspectRatio > 1) {
        // Landscape: adjust height
        frameHeight = frameWidth / aspectRatio
      }
      else {
        // Portrait: adjust width
        frameWidth = frameHeight * aspectRatio
      }
    }

    return { aspectRatio, frameWidth, frameHeight }
  }

  /**
   * Create the front and back faces of the canvas
   * @param {THREE.Texture} imageTexture - Image for front face
   * @param {number} frameWidth - Canvas width
   * @param {number} frameHeight - Canvas height
   */
  createCanvasFaces(imageTexture, frameWidth, frameHeight) {
    // Front face material - use MeshBasicMaterial for accurate color reproduction
    const frontMaterial = imageTexture
      ? new THREE.MeshBasicMaterial({ map: imageTexture })
      : new THREE.MeshBasicMaterial({ color: 0x111111 })

    // Back face material (black canvas backing)
    const backMaterial = new THREE.MeshLambertMaterial({ color: 0x111111 })

    // Front face (main image display)
    const frontGeometry = new THREE.PlaneGeometry(frameWidth, frameHeight)
    const frontMesh = new THREE.Mesh(frontGeometry, frontMaterial)
    frontMesh.position.z = this.frameDepth / 2 // Position at front of canvas
    this.frameGroup.add(frontMesh)

    // Back face (canvas backing)
    const backGeometry = new THREE.PlaneGeometry(frameWidth, frameHeight)
    const backMesh = new THREE.Mesh(backGeometry, backMaterial)
    backMesh.position.z = -this.frameDepth / 2 // Position at back of canvas
    backMesh.rotation.y = Math.PI // Rotate 180° to face outward from back
    this.frameGroup.add(backMesh)
  }

  /**
   * Create mirrored side edges using image edge pixels
   * Takes edge pixels from the image and mirrors them to create realistic canvas sides
   * @param {THREE.Texture} imageTexture - Source image for mirroring
   * @param {number} frameWidth - Canvas width
   * @param {number} frameHeight - Canvas height
   */
  createMirroredSides(imageTexture, frameWidth, frameHeight) {
    // Top side (mirrored from top edge of image)
    const topCanvas = this.createMirroredTexture(imageTexture.image, 'top', frameWidth)
    const topTexture = new THREE.CanvasTexture(topCanvas)
    topTexture.colorSpace = THREE.SRGBColorSpace
    const topMaterial = new THREE.MeshLambertMaterial({ map: topTexture })
    const topGeometry = new THREE.PlaneGeometry(frameWidth, this.frameDepth)
    const topMesh = new THREE.Mesh(topGeometry, topMaterial)
    topMesh.position.y = frameHeight / 2
    topMesh.rotation.x = -Math.PI / 2 // Rotate to face upward
    this.frameGroup.add(topMesh)

    // Bottom side (mirrored from bottom edge)
    const bottomCanvas = this.createMirroredTexture(imageTexture.image, 'bottom', frameWidth)
    const bottomTexture = new THREE.CanvasTexture(bottomCanvas)
    bottomTexture.colorSpace = THREE.SRGBColorSpace
    const bottomMaterial = new THREE.MeshLambertMaterial({ map: bottomTexture })
    const bottomGeometry = new THREE.PlaneGeometry(frameWidth, this.frameDepth)
    const bottomMesh = new THREE.Mesh(bottomGeometry, bottomMaterial)
    bottomMesh.position.y = -frameHeight / 2
    bottomMesh.rotation.x = Math.PI / 2 // Rotate to face downward
    this.frameGroup.add(bottomMesh)

    // Left side (mirrored from left edge)
    const leftCanvas = this.createMirroredTexture(imageTexture.image, 'left', frameHeight)
    const leftTexture = new THREE.CanvasTexture(leftCanvas)
    leftTexture.colorSpace = THREE.SRGBColorSpace
    const leftMaterial = new THREE.MeshLambertMaterial({ map: leftTexture })
    const leftGeometry = new THREE.PlaneGeometry(this.frameDepth, frameHeight)
    const leftMesh = new THREE.Mesh(leftGeometry, leftMaterial)
    leftMesh.position.x = -frameWidth / 2
    leftMesh.rotation.y = -Math.PI / 2 // Rotate to face left
    this.frameGroup.add(leftMesh)

    // Right side (mirrored from right edge)
    const rightCanvas = this.createMirroredTexture(imageTexture.image, 'right', frameHeight)
    const rightTexture = new THREE.CanvasTexture(rightCanvas)
    rightTexture.colorSpace = THREE.SRGBColorSpace
    const rightMaterial = new THREE.MeshLambertMaterial({ map: rightTexture })
    const rightGeometry = new THREE.PlaneGeometry(this.frameDepth, frameHeight)
    const rightMesh = new THREE.Mesh(rightGeometry, rightMaterial)
    rightMesh.position.x = frameWidth / 2
    rightMesh.rotation.y = Math.PI / 2 // Rotate to face right
    this.frameGroup.add(rightMesh)
  }

  /**
   * Create solid color side edges (black, white, or gray)
   * @param {number} frameWidth - Canvas width
   * @param {number} frameHeight - Canvas height
   * @param {string} sideType - Color type: 'black', 'white', or default gray
   */
  createSolidSides(frameWidth, frameHeight, sideType) {
    // Determine edge color based on side type
    let sideColor
    switch (sideType) {
      case 'black':
        sideColor = 0x111111 // Very dark
        break
      case 'white':
        sideColor = 0xffffff // Pure white
        break
      default:
        sideColor = 0x666666 // Medium gray
    }

    const sideMaterial = new THREE.MeshLambertMaterial({ color: sideColor })

    // Create top and bottom edges
    const topBottomSides = [
      { name: 'top', yPos: frameHeight / 2, rotation: -Math.PI / 2 },
      { name: 'bottom', yPos: -frameHeight / 2, rotation: Math.PI / 2 },
    ]

    topBottomSides.forEach(({ yPos, rotation }) => {
      const sideGeometry = new THREE.PlaneGeometry(frameWidth, this.frameDepth)
      const sideMesh = new THREE.Mesh(sideGeometry, sideMaterial)
      sideMesh.position.y = yPos
      sideMesh.rotation.x = rotation
      this.frameGroup.add(sideMesh)
    })

    // Create left and right edges
    const leftRightSides = [
      { name: 'left', xPos: -frameWidth / 2, rotation: -Math.PI / 2 },
      { name: 'right', xPos: frameWidth / 2, rotation: Math.PI / 2 },
    ]

    leftRightSides.forEach(({ xPos, rotation }) => {
      const sideGeometry = new THREE.PlaneGeometry(this.frameDepth, frameHeight)
      const sideMesh = new THREE.Mesh(sideGeometry, sideMaterial)
      sideMesh.position.x = xPos
      sideMesh.rotation.y = rotation
      this.frameGroup.add(sideMesh)
    })
  }

  /**
   * Create a mirrored texture from image edge pixels
   * Extracts a strip of pixels from the specified edge and mirrors it
   * @param {HTMLImageElement} image - Source image
   * @param {string} side - Which edge: 'top', 'bottom', 'left', 'right'
   * @param {number} frameDim1 - Frame dimension perpendicular to the edge
   * @returns {HTMLCanvasElement} Canvas with mirrored texture
   */
  createMirroredTexture(image, side, frameDim1) {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')

    const pixelsPerUnit = 300 // Texture resolution
    const imgWidth = image.width
    const imgHeight = image.height

    // Calculate source strip size (5% of smaller image dimension)
    const baseSourceStripSize = Math.max(1, Math.floor(Math.min(imgWidth, imgHeight) * 0.05))

    // Set canvas dimensions based on which side we're creating
    let finalCanvasWidth, finalCanvasHeight

    if (side === 'top' || side === 'bottom') {
      finalCanvasWidth = frameDim1 * pixelsPerUnit
      finalCanvasHeight = baseSourceStripSize
    }
    else { // 'left' or 'right'
      finalCanvasWidth = baseSourceStripSize
      finalCanvasHeight = frameDim1 * pixelsPerUnit
    }

    canvas.width = finalCanvasWidth
    canvas.height = finalCanvasHeight

    const destDrawWidth = finalCanvasWidth
    const destDrawHeight = finalCanvasHeight

    // Extract and mirror the appropriate edge
    switch (side) {
      case 'top':
        // Take top edge pixels and flip vertically
        ctx.save()
        ctx.scale(1, -1) // Flip vertically for mirror effect
        ctx.drawImage(
          image,
          0, 0, imgWidth, baseSourceStripSize, // Source: top strip
          0, -destDrawHeight, destDrawWidth, destDrawHeight, // Destination: full canvas
        )
        ctx.restore()
        break

      case 'bottom':
        // Take bottom edge pixels and flip vertically
        ctx.save()
        ctx.scale(1, -1)
        ctx.drawImage(
          image,
          0, imgHeight - baseSourceStripSize, imgWidth, baseSourceStripSize, // Source: bottom strip
          0, -destDrawHeight, destDrawWidth, destDrawHeight,
        )
        ctx.restore()
        break

      case 'left':
        // Take left edge pixels and flip horizontally
        ctx.save()
        ctx.translate(destDrawWidth, 0)
        ctx.scale(-1, 1) // Flip horizontally for mirror effect
        ctx.drawImage(
          image,
          0, 0, baseSourceStripSize, imgHeight, // Source: left strip
          0, 0, destDrawWidth, destDrawHeight,
        )
        ctx.restore()
        break

      case 'right':
        // Take right edge pixels and flip horizontally
        ctx.save()
        ctx.translate(destDrawWidth, 0)
        ctx.scale(-1, 1)
        ctx.drawImage(
          image,
          imgWidth - baseSourceStripSize, 0, baseSourceStripSize, imgHeight, // Source: right strip
          0, 0, destDrawWidth, destDrawHeight,
        )
        ctx.restore()
        break
    }

    return canvas
  }

  // ===== STIMULUS ACTION METHODS =====

  /**
   * Handle image upload from file input
   * Called when user selects a new image file
   * Only used when no src value is provided
   */
  imageInputChanged() {
    // Don't allow file input if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    if (!this.hasImageInputTarget) {
      return
    }

    const file = this.imageInputTarget.files[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          // Create Three.js texture with proper color settings
          const texture = new THREE.Texture(img)
          texture.needsUpdate = true
          texture.colorSpace = THREE.SRGBColorSpace
          texture.flipY = true // Proper orientation
          texture.premultiplyAlpha = false
          texture.format = THREE.RGBAFormat // Required for sRGB color space
          texture.type = THREE.UnsignedByteType // Required for sRGB

          this.currentTexture = texture

          // Rebuild canvas with new image
          const currentSideType = this.sideTypeValue
          this.createFrame(texture, currentSideType)
        }
        img.src = e.target.result
      }
      reader.readAsDataURL(file)
    }
  }

  /**
   * Handle side type selection change
   * Called when user changes edge treatment (mirrored/black/white)
   */
  sideTypeChanged() {
    if (!this.hasSideTypeSelectTarget) {
      return
    }

    const selectedSideType = this.sideTypeSelectTarget.value
    this.createFrame(this.currentTexture, selectedSideType)
  }

  /**
   * Handle depth slider change
   * Called when user adjusts canvas thickness
   */
  depthChanged() {
    if (!this.hasDepthSliderTarget) {
      return
    }

    const newDepth = parseFloat(this.depthSliderTarget.value)
    this.frameDepth = newDepth

    // Update display value if target exists
    if (this.hasDepthValueTarget) {
      this.depthValueTarget.textContent = newDepth.toFixed(2)
    }

    // Rebuild canvas with new depth
    const currentSideType = this.hasSideTypeSelectTarget ? this.sideTypeSelectTarget.value : 'mirrored'
    this.createFrame(this.currentTexture, currentSideType)
  }

  // ===== INTERACTION CONTROLS =====

  /**
   * Setup mouse, touch, and wheel event listeners for canvas interaction
   */
  setupControls() {
    // Mouse events
    this.element.addEventListener('mousedown', this.onMouseDown)
    this.element.addEventListener('mousemove', this.onMouseMove)
    this.element.addEventListener('mouseup', this.onMouseUp)
    this.element.addEventListener('wheel', this.onWheel)
    // canvas.addEventListener('contextmenu', e => e.preventDefault()) // Disable right-click menu

    // Touch events
    this.element.addEventListener('touchstart', this.onTouchStart, { passive: false })
    this.element.addEventListener('touchmove', this.onTouchMove, { passive: false })
    this.element.addEventListener('touchend', this.onTouchEnd, { passive: false })
  }

  // ===== MOUSE EVENT HANDLERS =====

  /**
   * Handle mouse press - start interaction and pause auto-rotation
   * Left click: rotation, Right click: panning
   */
  onMouseDown = (event) => {
    this.isDragging = true
    this.mouseButton = event.button // 0 = left, 2 = right
    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY,
    }

    // Temporarily disable auto-rotation during manual interaction
    this.autoRotate = false
  }

  /**
   * Handle mouse release - end interaction and resume auto-rotation
   */
  onMouseUp = () => {
    this.isDragging = false

    // Resume auto-rotation after a short delay
    setTimeout(() => {
      if (!this.isDragging && !this.isRotating && !this.isPanning && !this.isPinching) {
        this.autoRotate = true
      }
    }, 2000) // Wait 2 seconds before resuming auto-rotation
  }

  /**
   * Handle mouse movement during drag
   * Rotates or pans the canvas based on which mouse button is pressed
   */
  onMouseMove = (event) => {
    if (!this.isDragging) return

    const deltaMove = {
      x: event.clientX - this.previousMousePosition.x,
      y: event.clientY - this.previousMousePosition.y,
    }

    if (this.mouseButton === 0) {
      // Left mouse button - rotate canvas
      const rotationSpeed = 0.005
      this.frameGroup.rotation.y += deltaMove.x * rotationSpeed // Horizontal rotation
      this.frameGroup.rotation.x += deltaMove.y * rotationSpeed // Vertical rotation
    }
    else if (this.mouseButton === 2) {
      // Right mouse button - pan canvas
      const panSpeed = 0.002
      this.frameGroup.position.x += deltaMove.x * panSpeed
      this.frameGroup.position.y -= deltaMove.y * panSpeed // Invert Y for natural feel
    }

    this.previousMousePosition = {
      x: event.clientX,
      y: event.clientY,
    }
  }

  /**
   * Handle mouse wheel for zooming
   * Moves camera closer/farther from canvas
   */
  onWheel = (event) => {
    const zoomSpeed = 0.1
    this.camera.position.z += event.deltaY * 0.01 * zoomSpeed
    // Constrain zoom between 1 and 10 units
    this.camera.position.z = Math.max(1, Math.min(10, this.camera.position.z))
  }

  // ===== TOUCH EVENT HANDLERS =====

  /**
   * Handle touch start - supports single touch rotation, two-finger pan, and pinch zoom
   */
  onTouchStart = (e) => {
    e.preventDefault() // Prevent scrolling and other default behaviors

    // Clear existing touches and track new ones
    this.touches.clear()

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      this.touches.set(touch.identifier, {
        x: touch.clientX,
        y: touch.clientY,
        startX: touch.clientX,
        startY: touch.clientY,
      })
    }

    const touchCount = this.touches.size

    // Reset all touch states
    this.isRotating = false
    this.isPanning = false
    this.isPinching = false
    this.autoRotate = false // Pause auto-rotation

    if (touchCount === 1) {
      // Single touch - rotation mode
      this.isRotating = true
      this.touchInteractionType = 'rotate'

      const firstTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: firstTouch.x, y: firstTouch.y }
    }
    else if (touchCount === 2) {
      // Two touches - determine if it's panning or pinching based on initial movement
      this.touchInteractionType = 'pending' // Will be determined in touchmove

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate initial distance between touches for pinch detection
      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )

      // Calculate center point for panning
      this.previousTouchPosition = {
        x: (touch1.x + touch2.x) / 2,
        y: (touch1.y + touch2.y) / 2,
      }
    }
  }

  /**
   * Handle touch move - performs rotation, panning, or pinch zoom based on touch count and gesture
   */
  onTouchMove = (e) => {
    e.preventDefault() // Prevent scrolling

    // Update touch positions
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      if (this.touches.has(touch.identifier)) {
        const storedTouch = this.touches.get(touch.identifier)
        storedTouch.x = touch.clientX
        storedTouch.y = touch.clientY
        this.touches.set(touch.identifier, storedTouch)
      }
    }

    const touchCount = this.touches.size

    if (touchCount === 1 && this.isRotating && this.frameGroup) {
      // Single touch rotation
      const currentTouch = Array.from(this.touches.values())[0]

      const deltaMove = {
        x: currentTouch.x - this.previousTouchPosition.x,
        y: currentTouch.y - this.previousTouchPosition.y,
      }

      // Apply rotation with touch-friendly sensitivity
      const rotationSpeed = 0.006 // Slightly more sensitive than mouse
      this.frameGroup.rotation.y += deltaMove.x * rotationSpeed
      this.frameGroup.rotation.x += deltaMove.y * rotationSpeed

      // Update previous position
      this.previousTouchPosition = { x: currentTouch.x, y: currentTouch.y }
    }
    else if (touchCount === 2) {
      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate current distance between touches
      const currentDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )

      // If interaction type is pending, determine based on movement
      if (this.touchInteractionType === 'pending') {
        const distanceChange = Math.abs(currentDistance - this.lastPinchDistance)
        const centerX = (touch1.x + touch2.x) / 2
        const centerY = (touch1.y + touch2.y) / 2
        const centerMovement = Math.sqrt(
          Math.pow(centerX - this.previousTouchPosition.x, 2)
          + Math.pow(centerY - this.previousTouchPosition.y, 2),
        )

        // Determine gesture type based on which movement is more significant
        if (distanceChange > centerMovement && distanceChange > 5) {
          // Distance change is more significant - pinch zoom
          this.touchInteractionType = 'pinch'
          this.isPinching = true
        }
        else if (centerMovement > 5) {
          // Center movement is more significant - panning
          this.touchInteractionType = 'pan'
          this.isPanning = true
        }
      }

      if (this.touchInteractionType === 'pinch' && this.isPinching) {
        // Two-finger pinch zoom
        const deltaDistance = currentDistance - this.lastPinchDistance
        const zoomSensitivity = 0.01

        // Apply zoom to camera
        this.camera.position.z -= deltaDistance * zoomSensitivity
        this.camera.position.z = Math.max(1, Math.min(this.camera.position.z, 10))

        // Update last distance for next calculation
        this.lastPinchDistance = currentDistance
      }
      else if (this.touchInteractionType === 'pan' && this.isPanning && this.frameGroup) {
        // Two-finger panning
        const centerX = (touch1.x + touch2.x) / 2
        const centerY = (touch1.y + touch2.y) / 2

        const deltaMove = {
          x: centerX - this.previousTouchPosition.x,
          y: centerY - this.previousTouchPosition.y,
        }

        // Apply panning with touch-friendly sensitivity
        const panSpeed = 0.003 // Slightly more sensitive than mouse
        this.frameGroup.position.x += deltaMove.x * panSpeed
        this.frameGroup.position.y -= deltaMove.y * panSpeed // Invert Y for natural feel

        // Update previous center position
        this.previousTouchPosition = { x: centerX, y: centerY }
      }
    }
  }

  /**
   * Handle touch end - cleanup touch tracking and resume auto-rotation
   */
  onTouchEnd = (e) => {
    e.preventDefault()

    // Remove ended touches from tracking
    const activeTouchIds = Array.from(e.touches).map(touch => touch.identifier)

    // Remove touches that are no longer active
    for (let [touchId] of this.touches) {
      if (!activeTouchIds.includes(touchId)) {
        this.touches.delete(touchId)
      }
    }

    const remainingTouches = this.touches.size

    if (remainingTouches === 0) {
      // All touches ended
      this.isRotating = false
      this.isPanning = false
      this.isPinching = false
      this.touchInteractionType = null

      // Resume auto-rotation after a delay
      setTimeout(() => {
        if (!this.isRotating && !this.isPanning && !this.isPinching && !this.isDragging) {
          this.autoRotate = true
        }
      }, 2000) // Wait 2 seconds before resuming auto-rotation
    }
    else if (remainingTouches === 1) {
      // Transitioned to single touch - switch to rotation
      this.isPanning = false
      this.isPinching = false
      this.isRotating = true
      this.touchInteractionType = 'rotate'

      const remainingTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: remainingTouch.x, y: remainingTouch.y }
    }
    else if (remainingTouches === 2 && this.isRotating) {
      // Added second touch - reset to pending state
      this.isRotating = false
      this.touchInteractionType = 'pending'

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )

      this.previousTouchPosition = {
        x: (touch1.x + touch2.x) / 2,
        y: (touch1.y + touch2.y) / 2,
      }
    }
  }

  // ===== ANIMATION AND EVENTS =====

  /**
   * Main animation loop - renders the scene continuously
   */
  animate() {
    requestAnimationFrame(this.animate.bind(this))

    // Auto-rotate the frame group if enabled and not being manually interacted with
    if (this.autoRotate && !this.isDragging && !this.isRotating && !this.isPanning && !this.isPinching && this.frameGroup) {
      this.frameGroup.rotation.y += this.autoRotateSpeed
    }

    this.renderer.render(this.scene, this.camera)
  }

  /**
   * Handle window resize - update camera and renderer
   */
  onWindowResize = () => {
    this.camera.aspect = this.containerTarget.clientWidth / this.containerTarget.clientHeight
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(this.containerTarget.clientWidth, this.containerTarget.clientHeight)

    // Recalculate optimal camera position for new container size
    if (this.frameGroup) {
    // Get current canvas dimensions
      const { frameWidth, frameHeight } = this.getCurrentCanvasDimensions()
      this.adjustCameraForCanvas(frameWidth, frameHeight)
    }
  }

  /**
   * Get current canvas dimensions (helper method for resize handling)
   */
  getCurrentCanvasDimensions() {
    if (this.currentTexture && this.currentTexture.image) {
      return this.calculateDimensions(this.currentTexture)
    }
    return { frameWidth: 2, frameHeight: 2 } // Default dimensions
  }
}
